import React, { useState, useRef, useEffect, useCallback } from 'react';
import { devLog } from '../utils/devTools';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Container,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DiagnosticService from '../services/DiagnosticService';
import { PasskeyService } from '../services/PasskeyService';
import PasskeyEnrollmentPrompt, { shouldShowPasskeyPrompt } from '../components/PasskeyEnrollmentPrompt';

// Declare the Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId?: string) => void;
      ready?: (callback: () => void) => void;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: string | ((token: string) => void);
  'expired-callback'?: string | (() => void);
  'error-callback'?: string | ((error: unknown) => void);
  theme?: 'light' | 'dark' | 'auto';
  tabindex?: number;
  'response-field'?: boolean;
}

const LoginPage: React.FC = () => {
  const {
    isAuthenticated,
    loading: authLoading,
    email,
    setEmail,
    loginWithEmail,
    loginWithPasskey,
    verifyToken,
    resendToken,
    emailSubmitted,
    setEmailSubmitted,
    passkeyAvailable,
    reloadAuthStatus
  } = useAuth();

  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileRetrying, setTurnstileRetrying] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileRetryCount = useRef<number>(0);
  const turnstileRetryTimeout = useRef<NodeJS.Timeout | null>(null);

  // Safely reset the Turnstile widget. Cloudflare's reset() throws
  // "Nothing to reset found for provided container" when the widget's
  // container has already been unmounted (e.g. after the email step is
  // submitted and the <div ref={turnstileRef}/> is gone). Swallow that so
  // it doesn't surface as an uncaught runtime error.
  const safeResetTurnstile = useCallback(() => {
    if (window.turnstile && turnstileWidgetId.current) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (resetError) {
        console.warn('[LoginPage] Error resetting Turnstile widget (non-fatal):', resetError);
      }
    }
  }, []);

  // Passkey-related state
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [hasRegisteredPasskeys, setHasRegisteredPasskeys] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(false);
  const [showEnrollmentPrompt, setShowEnrollmentPrompt] = useState(false);
  const [authenticatingPasskey, setAuthenticatingPasskey] = useState(false);
  const [conditionalAuthFailed, setConditionalAuthFailed] = useState(false);
  const [processingEmailFallback, setProcessingEmailFallback] = useState(false);
  const conditionalAuthAbortController = useRef<AbortController | null>(null);
  const conditionalAuthStarted = useRef<boolean>(false);
  const conditionalAuthInteractionDetected = useRef<boolean>(false);
  const emailFieldFocusCount = useRef<number>(0);
  const emailSubmittedRef = useRef<boolean>(false);
  const showPasskeyPromptRef = useRef<boolean>(false);

  // Keep refs in sync with state
  useEffect(() => {
    emailSubmittedRef.current = emailSubmitted;
  }, [emailSubmitted]);

  useEffect(() => {
    showPasskeyPromptRef.current = showPasskeyPrompt;
  }, [showPasskeyPrompt]);

  // Redirect to jobs page if already authenticated
  // But don't redirect if we're showing the enrollment prompt
  useEffect(() => {
    if (isAuthenticated && !showEnrollmentPrompt) {
      navigate('/jobs');
    }
  }, [isAuthenticated, showEnrollmentPrompt, navigate]);

  // Suppress async TurnstileError rejections from Cloudflare's api.js so they don't
  // reach React's reportError path and force the LoginPage tree to unmount (white page).
  // The same errors are still logged via console.warn for visibility.
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const name = reason?.name || '';
      const message = typeof reason === 'string' ? reason : reason?.message || '';
      if (name === 'TurnstileError' || /Turnstile/i.test(message)) {
        console.warn('[LoginPage] Suppressed Turnstile unhandled rejection:', reason);
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  // Dynamically load Turnstile script only when needed
  useEffect(() => {
    devLog('[DEBUG LoginPage] Turnstile load useEffect triggered', { isAuthenticated, emailSubmitted, showPasskeyPrompt });

    // Skip if already authenticated, or if user has moved past the email step
    // (Turnstile has already been passed at that point — no need to load the script).
    if (isAuthenticated || emailSubmitted || showPasskeyPrompt) return;

    // Check if script is already loaded
    if (window.turnstile) {
      setTurnstileLoaded(true);
      turnstileRetryCount.current = 0;
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      devLog('[DEBUG LoginPage] Turnstile script found in DOM, waiting for load');
      // Script exists, wait for it to load
      const checkTurnstile = () => {
        if (window.turnstile) {
          setTurnstileLoaded(true);
          turnstileRetryCount.current = 0;
        }
      };
      const intervalId = setInterval(checkTurnstile, 100);
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        if (!window.turnstile) {
          DiagnosticService.logTurnstileIssue('script_load_timeout', {
            protocol: window.location.protocol,
            scriptSrc: 'https://challenges.cloudflare.com/turnstile/v0/api.js',
            retryCount: turnstileRetryCount.current
          });
          setErrorMessage('Security verification service is unavailable. Please try again later.');
        }
      }, 5000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 4000, 8000]; // Exponential backoff: 2s, 4s, 8s

    const cleanupTurnstileResources = () => {
      devLog('[DEBUG LoginPage] Cleaning up Turnstile resources before retry');

      // 1. Remove all existing Turnstile script tags (in case multiple exist)
      const existingScripts = document.querySelectorAll('script[src*="turnstile"]');
      existingScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });

      // 2. Clear any existing widget instance
      if (window.turnstile && turnstileWidgetId.current) {
        try {
          window.turnstile.reset(turnstileWidgetId.current);
        } catch (error) {
          console.warn('[DEBUG LoginPage] Error resetting Turnstile widget:', error);
        }
        turnstileWidgetId.current = null;
      }

      // 3. Clear the widget container DOM
      if (turnstileRef.current) {
        turnstileRef.current.innerHTML = '';
      }

      // 4. Clear token state
      setTurnstileToken(null);

      // Note: We intentionally do NOT delete window.turnstile itself
      // because if it loaded partially, removing it could cause errors
      // Instead, we let the new script attempt to reinitialize it
    };

    const loadTurnstileScript = () => {
      const attemptNumber = turnstileRetryCount.current + 1;
      devLog('[DEBUG LoginPage] Loading Turnstile script (attempt', attemptNumber, 'of', MAX_RETRIES + 1, ')');

      if (attemptNumber > 1) {
        setTurnstileRetrying(true);
        // Clean up resources from previous failed attempts before retrying
        cleanupTurnstileResources();
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      // Turnstile requires async/defer to be false when using turnstile.ready()
      script.async = false;
      script.defer = false;

      script.onload = () => {
        devLog('[DEBUG LoginPage] Turnstile script loaded successfully on attempt', attemptNumber);
        setTurnstileLoaded(true);
        setTurnstileRetrying(false);
        turnstileRetryCount.current = 0;

        // Log successful retry to track recovery patterns
        if (attemptNumber > 1) {
          DiagnosticService.logTurnstileIssue('script_load_retry_success', {
            protocol: window.location.protocol,
            scriptSrc: script.src,
            retryCount: attemptNumber - 1,
            successfulAttempt: attemptNumber
          });
        }
      };

      script.onerror = () => {
        console.error('[DEBUG LoginPage] Turnstile script failed to load (attempt', attemptNumber, ')');

        // Log the failure with retry information
        DiagnosticService.logTurnstileIssue('script_load_failed', {
          protocol: window.location.protocol,
          scriptSrc: script.src,
          retryCount: turnstileRetryCount.current,
          attemptNumber,
          maxRetries: MAX_RETRIES
        });

        // Clean up failed script
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }

        // Retry logic
        if (turnstileRetryCount.current < MAX_RETRIES) {
          const delay = RETRY_DELAYS[turnstileRetryCount.current];
          devLog('[DEBUG LoginPage] Retrying in', delay, 'ms...');

          turnstileRetryCount.current++;
          turnstileRetryTimeout.current = setTimeout(() => {
            loadTurnstileScript();
          }, delay);
        } else {
          // All retries exhausted
          console.error('[DEBUG LoginPage] All Turnstile load retries exhausted');
          setTurnstileRetrying(false);
          setErrorMessage('Security verification service failed to load after multiple attempts. Please refresh the page or try again later.');

          DiagnosticService.logTurnstileIssue('script_load_all_retries_failed', {
            protocol: window.location.protocol,
            scriptSrc: script.src,
            totalAttempts: MAX_RETRIES + 1
          });
        }
      };

      document.head.appendChild(script);
      devLog('[DEBUG LoginPage] Turnstile script appended to head (attempt', attemptNumber, ')');
    };

    // Start the initial load
    loadTurnstileScript();

    return () => {
      // Clear any pending retry timeouts
      if (turnstileRetryTimeout.current) {
        clearTimeout(turnstileRetryTimeout.current);
        turnstileRetryTimeout.current = null;
      }
      // Note: We don't reset turnstileRetryCount.current here to preserve it across re-renders
      // We only reset it on successful load
    };
  }, [isAuthenticated, emailSubmitted, showPasskeyPrompt]);

  // Initialize Turnstile widget when script is loaded and component is ready.
  // Skip when the email step is already past — the <div ref={turnstileRef}/> is
  // unmounted in that case, and touching a stale widget triggers Cloudflare's
  // "Nothing to reset found" error which React treats as a fatal commit error.
  useEffect(() => {
    if (!turnstileLoaded || isAuthenticated || emailSubmitted || showPasskeyPrompt) {
      return;
    }

    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 20; // Max 2 seconds of retries (20 * 100ms)

    const initializeTurnstile = () => {
      if (!isMounted) return;

      if (window.turnstile && turnstileRef.current) {
        try {
          // Clear any existing widget and DOM contents
          if (turnstileWidgetId.current) {
            try {
              window.turnstile.reset(turnstileWidgetId.current);
            } catch (resetError) {
              console.warn('[DEBUG LoginPage] Error resetting stale Turnstile widget (non-fatal):', resetError);
            }
            turnstileWidgetId.current = null;
          }

          // Clear the container of any existing elements
          if (turnstileRef.current) {
            turnstileRef.current.innerHTML = '';
          }

          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: window.APP_CONFIG?.turnstileSiteKey || "1x00000000000000000000AA",
            callback: (token: string) => {
              if (isMounted) {
                setTurnstileToken(token);
              }
            },
            'expired-callback': () => {
              if (isMounted) {
                setTurnstileToken(null);
              }
            },
            'error-callback': (error: unknown) => {
              if (isMounted) {
                console.error('Turnstile error:', error);
                setErrorMessage('Security verification failed. Please try again.');
                setTurnstileToken(null);

                DiagnosticService.logTurnstileIssue('error_callback', {
                  error,
                  protocol: window.location.protocol,
                  sitekey: window.APP_CONFIG?.turnstileSiteKey || "default"
                });
              }
            },
            theme: 'light'
          });
        } catch (error) {
          console.error('Error rendering Turnstile widget:', error);
          if (isMounted) {
            setErrorMessage('Failed to initialize security verification.');

            DiagnosticService.logTurnstileIssue('initialization_failed', {
              error: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined,
              protocol: window.location.protocol,
              sitekey: window.APP_CONFIG?.turnstileSiteKey || "default",
              turnstileAvailable: !!window.turnstile,
              refAvailable: !!turnstileRef.current
            });
          }
        }
      } else if (!turnstileRef.current && retryCount < MAX_RETRIES) {
        // If ref is not ready, try again but limit retries
        retryCount++;
        setTimeout(initializeTurnstile, 100);
      }
    };

    // Use turnstile.ready() if available
    if (window.turnstile?.ready) {
      try {
        window.turnstile.ready(() => {
          if (isMounted) {
            setTimeout(initializeTurnstile, 50);
          }
        });
      } catch (error) {
        console.error('Error with turnstile.ready():', error);
        // Fallback to timeout approach
        setTimeout(initializeTurnstile, 100);
      }
    } else {
      // Add a small delay to ensure React has rendered the ref
      setTimeout(initializeTurnstile, 100);
    }

    // Cleanup function to reset when component unmounts or effect reruns
    return () => {
      isMounted = false;
      if (window.turnstile && turnstileWidgetId.current) {
        try {
          window.turnstile.reset(turnstileWidgetId.current);
        } catch (resetError) {
          console.warn('[DEBUG LoginPage] Error resetting Turnstile widget on cleanup (non-fatal):', resetError);
        }
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileLoaded, isAuthenticated, emailSubmitted, showPasskeyPrompt]);

  // Reset conditional auth flag on component unmount
  useEffect(() => {
    return () => {
      conditionalAuthStarted.current = false;
    };
  }, []);

  // Start conditional UI (autofill) passkey authentication on mount
  useEffect(() => {
    devLog('[DEBUG LoginPage] Conditional auth useEffect triggered', {
      isAuthenticated,
      passkeyAvailable,
      emailSubmitted,
      showPasskeyPrompt,
      userAgent: navigator.userAgent,
      browser: navigator.userAgent.includes('Safari') ? 'Safari' : 'Other',
      conditionalAuthStarted: conditionalAuthStarted.current
    });

    if (isAuthenticated) {
      devLog('[DEBUG LoginPage] Skipping conditional auth - already authenticated');
      return;
    }

    if (!passkeyAvailable) {
      devLog('[DEBUG LoginPage] Skipping conditional auth - passkeyAvailable is false');
      return;
    }

    if (conditionalAuthStarted.current) {
      devLog('[DEBUG LoginPage] Skipping conditional auth - already started (prevents double init)');
      return;
    }

    conditionalAuthStarted.current = true;
    let isMounted = true;
    let pollingInterval: NodeJS.Timeout | null = null;

    const startConditionalAuth = async () => {
      try {
        devLog('[DEBUG LoginPage] Starting conditional authentication...');
        const { PasskeyService } = await import('../services/PasskeyService');

        // Wait for DOM to be fully ready before starting conditional auth
        // This ensures the email input with autocomplete="webauthn" is in the DOM
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        devLog('[DEBUG LoginPage] DOM ready after requestAnimationFrame');

        // Additional safety check: Verify the email input with webauthn autocomplete exists
        const emailInput = document.querySelector('input[name="email"][autocomplete*="webauthn"]');
        if (!emailInput) {
          console.warn('[DEBUG LoginPage] Email input with webauthn autocomplete not found in DOM yet, skipping conditional auth');
          conditionalAuthStarted.current = false; // Reset flag so it can try again
          return;
        }
        devLog('[DEBUG LoginPage] Email input with webauthn autocomplete confirmed in DOM');

        // Safari workaround: Poll auth status while conditional auth is active
        // This detects when Safari completes passkey auth but doesn't resolve the promise
        devLog('[DEBUG LoginPage] Starting auth status polling (Safari workaround)');
        pollingInterval = setInterval(async () => {
          if (!isMounted) {
            devLog('[DEBUG LoginPage] Polling stopped - component unmounted');
            if (pollingInterval) clearInterval(pollingInterval);
            return;
          }

          // Stop polling if user submitted email for OTP flow
          if (emailSubmittedRef.current) {
            devLog('[DEBUG LoginPage] Polling stopped - user submitted email for OTP');
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
            return;
          }

          // Stop polling if user re-focused input (stuck promise detected)
          if (conditionalAuthInteractionDetected.current) {
            devLog('[DEBUG LoginPage] Polling stopped - user interaction detected (stuck promise)');
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
            return;
          }

          try {
            await reloadAuthStatus();
            // If we're now authenticated, the useEffect that redirects will handle navigation
          } catch (error) {
            console.error('[DEBUG LoginPage] Error polling auth status:', error);
          }
        }, 1000); // Poll every second

        const abortController = await PasskeyService.startConditionalAuthentication(
          async (authResponse) => {
            if (!isMounted) {
              return;
            }

            devLog('[DEBUG] Conditional authentication response received (promise resolved!)');
            setAuthenticatingPasskey(true);

            // Stop polling since we got the promise callback
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }

            try {
              // Verify the authentication response
              const result = await PasskeyService.verifyConditionalAuthentication(authResponse);
              devLog('[DEBUG] Verification result:', result);

              if (result.success) {
                devLog('[DEBUG] Conditional authentication successful');
                await reloadAuthStatus();
                navigate('/jobs');
              } else {
                console.error('[DEBUG] Conditional authentication failed:', result.message);
                setErrorMessage(result.message);
                setAuthenticatingPasskey(false);
              }
            } catch (error) {
              console.error('[DEBUG] Error during verification:', error);
              setErrorMessage('Authentication failed');
              setAuthenticatingPasskey(false);
            }
          },
          (error) => {
            if (!isMounted) {
              return;
            }
            // Don't show errors if user has already moved to email/passkey flow
            if (emailSubmittedRef.current || showPasskeyPromptRef.current) {
              devLog('[DEBUG] Conditional auth error - user already in auth flow, skipping error display');
              return;
            }
            console.error('[DEBUG] Conditional authentication error:', error);
            setErrorMessage(error);
          }
        );

        devLog('[DEBUG LoginPage] Conditional auth started, abort controller:', abortController);
        conditionalAuthAbortController.current = abortController;
      } catch (error) {
        console.error('[DEBUG LoginPage] Failed to start conditional auth:', error);
      }
    };

    startConditionalAuth();

    return () => {
      isMounted = false;
      // Don't reset conditionalAuthStarted flag - it should persist across re-renders
      // to prevent double initialization. It will be reset when component unmounts.

      // Stop polling
      if (pollingInterval) {
        devLog('[DEBUG LoginPage] Stopping polling interval');
        clearInterval(pollingInterval);
        pollingInterval = null;
      }

      // Abort conditional authentication when leaving the page
      if (conditionalAuthAbortController.current) {
        devLog('[DEBUG LoginPage] Aborting conditional auth controller');
        conditionalAuthAbortController.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, passkeyAvailable, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!turnstileToken) {
      setErrorMessage('Please complete the CAPTCHA verification');

      // Log missing token issue
      DiagnosticService.logTurnstileIssue('missing_token', {
        email: email.trim() ? 'email_provided' : 'email_missing',
        turnstileAvailable: !!window.turnstile,
        protocol: window.location.protocol
      });

      return;
    }

    // Check if this email has registered passkeys (email-first flow)
    if (passkeyAvailable) {
      setCheckingPasskeys(true);
      const hasPasskeys = await PasskeyService.hasPasskeys(email);

      if (hasPasskeys) {
        // User has passkeys - show passkey prompt
        setCheckingPasskeys(false);
        setHasRegisteredPasskeys(true);
        setShowPasskeyPrompt(true);
        setSuccessMessage('We found passkeys for this account. Please authenticate with your passkey.');
        return; // Don't proceed with email + OTP flow yet
      }
      // Keep checkingPasskeys true while sending OTP to avoid UI flash
    }

    // No passkeys found or passkeys not available - proceed with traditional email + OTP flow
    const result = await loginWithEmail(email, turnstileToken);
    setCheckingPasskeys(false); // Now safe to clear, loginWithEmail has set emailSubmitted

    // Reset focus count to prevent false positive stuck promise detection
    emailFieldFocusCount.current = 0;

    if (result.success) {
      setSuccessMessage(result.message);
      // Reset turnstile token after successful use
      setTurnstileToken(null);
      // Reset the widget
      safeResetTurnstile();
    } else {
      setErrorMessage(result.message);
      // Reset turnstile on error
      setTurnstileToken(null);
      safeResetTurnstile();
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenInput.trim()) {
      setErrorMessage('Please enter the verification token');
      return;
    }

    const result = await verifyToken(tokenInput);
    if (result.success) {
      setTokenInput('');
      setSuccessMessage('Successfully authenticated');

      // Check if we should show passkey enrollment prompt
      devLog('[DEBUG] Passkey enrollment check:', {
        passkeyAvailable,
        hasRegisteredPasskeys,
        shouldShow: shouldShowPasskeyPrompt(),
        willShowPrompt: passkeyAvailable && !hasRegisteredPasskeys && shouldShowPasskeyPrompt()
      });

      if (passkeyAvailable && !hasRegisteredPasskeys && shouldShowPasskeyPrompt()) {
        devLog('[DEBUG] Showing passkey enrollment prompt');
        setShowEnrollmentPrompt(true);
      } else {
        devLog('[DEBUG] Skipping passkey enrollment prompt');
        navigate('/jobs');
      }
    } else {
      setErrorMessage(result.message);
    }
  };

  const handleEnrollmentComplete = () => {
    setSuccessMessage('Passkey added successfully!');
    navigate('/jobs');
  };

  const handleResendToken = async () => {
    const result = await resendToken();
    if (result.success) {
      setSuccessMessage(result.message);
    } else {
      setErrorMessage(result.message);
    }
  };


  const handlePasskeyLogin = async () => {
    setAuthenticatingPasskey(true);
    setErrorMessage(null);
    try {
      const result = await loginWithPasskey(email);
      if (result.success) {
        // Don't clear authenticating state or show success message
        // Just navigate immediately - the jobs page will load
        navigate('/jobs');
      } else {
        setErrorMessage(result.message);
        setAuthenticatingPasskey(false);
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred');
      setAuthenticatingPasskey(false);
    }
  };

  const handleUseEmailInstead = async () => {
    // Shouldn't happen — submit button is disabled without a token, but guard just in case
    if (!turnstileToken) {
      setErrorMessage('Please complete the CAPTCHA verification');
      return;
    }
    // Fall back to email + OTP flow
    setProcessingEmailFallback(true);
    setShowPasskeyPrompt(false);
    const result = await loginWithEmail(email, turnstileToken);
    setProcessingEmailFallback(false);
    if (result.success) {
      setSuccessMessage(result.message);
      setTurnstileToken(null);
      safeResetTurnstile();
    } else {
      setErrorMessage(result.message);
      setTurnstileToken(null);
      safeResetTurnstile();
    }
  };

  const handleCloseMessage = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  // Show loading state while checking auth or during successful passkey authentication
  if (authLoading || (authenticatingPasskey && isAuthenticated) || processingEmailFallback || checkingPasskeys) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          {authLoading
            ? 'Checking authentication status...'
            : processingEmailFallback
              ? 'Sending verification code...'
              : checkingPasskeys
                ? 'Checking for passkeys...'
                : 'Signing you in...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
          {!emailSubmitted && !showPasskeyPrompt ? (
            <>
              <Typography variant="h5" gutterBottom>
                Court Reporter Login
              </Typography>
              <Typography variant="body1" mb={3}>
                Please enter your email address to receive a one-time verification token.
              </Typography>
              <form onSubmit={handleEmailSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  disabled={checkingPasskeys}
                  inputProps={{
                    autoComplete: 'username webauthn',
                  }}
                  onFocus={() => {
                    emailFieldFocusCount.current += 1;

                    // Safari workaround: If user re-focuses input after conditional auth started,
                    // it likely means they tried to use passkey but Safari's promise hung
                    if (conditionalAuthStarted.current && emailFieldFocusCount.current > 1 && !conditionalAuthFailed) {
                      devLog('[DEBUG] Detected re-focus after conditional auth started - Safari promise may be stuck');
                      devLog('[DEBUG] Showing fallback message to user');
                      setConditionalAuthFailed(true);
                      setErrorMessage('Passkey autofill did not respond. Please enter your email to continue with passkey or email verification.');

                      // Stop polling to save resources
                      conditionalAuthInteractionDetected.current = true;
                    }
                  }}
                />

                {/* Turnstile Widget */}
                <Box sx={{ my: 2, minHeight: '65px' }}>
                  {!turnstileLoaded ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="65px">
                      <CircularProgress size={20} />
                      <Typography variant="body2" ml={1}>
                        {turnstileRetrying
                          ? `Retrying security verification (attempt ${turnstileRetryCount.current + 1})...`
                          : 'Loading security verification...'}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <div ref={turnstileRef} />

                    </>
                  )}
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={!turnstileToken || !turnstileLoaded || checkingPasskeys}
                  startIcon={checkingPasskeys ? <CircularProgress size={20} /> : undefined}
                >
                  {checkingPasskeys ? 'Checking...' : 'Continue'}
                </Button>
              </form>
            </>
          ) : showPasskeyPrompt ? (
            <>
              <Typography variant="h5" gutterBottom>
                Sign In with Passkey
              </Typography>
              <Typography variant="body1" mb={3}>
                Use your passkey to sign in to {email}
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handlePasskeyLogin}
                  disabled={authenticatingPasskey}
                  startIcon={authenticatingPasskey ? <CircularProgress size={20} /> : <FingerprintIcon />}
                  size="large"
                >
                  {authenticatingPasskey ? 'Authenticating...' : 'Use Passkey'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleUseEmailInstead}
                  disabled={authenticatingPasskey}
                >
                  Use Email Verification Instead
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setShowPasskeyPrompt(false);
                    setHasRegisteredPasskeys(false);
                    setEmail('');
                    setTurnstileToken(null);
                    safeResetTurnstile();
                  }}
                >
                  Use a Different Email
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                Verify Your Email
              </Typography>
              <Typography variant="body1" mb={3}>
                We&apos;ve sent a verification token to {email}. Please enter it below.
              </Typography>
              <form onSubmit={handleTokenSubmit}>
                <TextField
                  fullWidth
                  label="Verification Token"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  margin="normal"
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Verify
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleResendToken}
                  sx={{ mb: 2 }}
                >
                  Resend Token
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setEmailSubmitted(false);
                    setEmail(''); // Clear the email field when switching
                    // Reset turnstile when using a different email
                    setTurnstileToken(null);
                    safeResetTurnstile();
                  }}
                >
                  Use a Different Email
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Container>
      <Footer />

      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Passkey Enrollment Prompt */}
      <PasskeyEnrollmentPrompt
        open={showEnrollmentPrompt}
        onClose={() => {
          setShowEnrollmentPrompt(false);
          navigate('/jobs');
        }}
        onEnrolled={handleEnrollmentComplete}
      />
    </Box>
  );
};

export default LoginPage;